import { useState, useEffect, useMemo } from 'react';
import {
    Search, RefreshCw, FileText,
    CheckCircle2, XCircle,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Plus,
    Edit2,
    Trash2,
    Eye,
    Monitor,
    X,
    AlertCircle
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import AddTemplateModal from './AddTemplateModal';
import { useClientLevel } from '../../hooks/useClientLevel';
import { hasPermission } from '../../utils/permissionsHelper';
import ColumnVisibilityDropdown, { type ColumnOption } from '../../components/ColumnVisibilityDropdown';

interface EventTemplate {
    id: number;
    name: string;
    event_type: string;
    is_global: boolean;
    is_active: boolean;
    created: string;
    updated: string;
    ticket_open_template: string;
    ticket_closure_template: string;
}

export type EventTemplateColumnKey =
    | 'name'
    | 'event_type'
    | 'is_global'
    | 'status'
    | 'created'
    | 'actions';

const EventTemplates = () => {
    const level = useClientLevel();
    const [templates, setTemplates] = useState<EventTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<EventTemplate | null>(null);
    const [previewingTemplate, setPreviewingTemplate] = useState<EventTemplate | null>(null);
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null }>({
        isOpen: false,
        id: null
    });
    const [deleting, setDeleting] = useState(false);
    const canCreate = hasPermission('events', 'create');
    const canUpdate = hasPermission('events', 'update');
    const canDelete = hasPermission('events', 'delete');

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<EventTemplateColumnKey, boolean>>({
        name: true,
        event_type: true,
        is_global: true,
        status: true,
        created: true,
        actions: true,
    });

    const columnOptions: ColumnOption<EventTemplateColumnKey>[] = useMemo(() => [
        { key: 'name', label: 'Template Name' },
        { key: 'event_type', label: 'Event Type' },
        { key: 'is_global', label: 'Global' },
        { key: 'status', label: 'Status' },
        { key: 'created', label: 'Created At' },
        { key: 'actions', label: 'Actions' },
    ], []);

    const visibleColsCount = useMemo(() => {
        let count = 0;
        if (visibleColumns.name) count++;
        if (visibleColumns.event_type) count++;
        if (visibleColumns.is_global) count++;
        if (visibleColumns.status) count++;
        if (visibleColumns.created) count++;
        if (visibleColumns.actions) count++;
        return count || 1;
    }, [visibleColumns]);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (level.name !== 'global') {
                queryObj.$or = [
                    { is_global: true },
                    { client_id: level.id }
                ];
            } else {
                queryObj.is_global = true;
            }
            const params: any = {
                skip: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
                ...(searchTerm && { search: searchTerm }),
            };
            params.query = JSON.stringify(queryObj);

            const response = await baseapi.get('/event_description_templates', { params });
            if (response.data.status) {
                setTemplates(response.data.data);
                setTotalItems(response.data.total);
            } else {
                showError(response.data.message || 'Failed to fetch templates');
            }
        } catch (error: any) {
            showError(error.message || 'Error occurred while fetching templates');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, [currentPage, itemsPerPage, searchTerm, level.id]);

    const handleRefresh = () => {
        fetchTemplates();
    };

    const handleDeleteTemplate = async () => {
        if (!deleteModal.id) return;
        setDeleting(true);
        try {
            const response = await baseapi.delete(`/event_description_templates/${deleteModal.id}`);
            if (response.data.status) {
                setTemplates(prev => prev.filter(t => t.id !== deleteModal.id));
                setTotalItems(prev => prev - 1);
                setDeleteModal({ isOpen: false, id: null });
            } else {
                showError(response.data.message || 'Failed to delete template');
                setDeleteModal({ isOpen: false, id: null });
            }
        } catch (error: any) {
            showError(error.message || 'Error occurred while deleting template');
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="flex flex-col bg-transparent text-gray-900 dark:text-gray-200">
            {/* Header Row */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-1.5">
                    <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                        <FileText className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none">Event Description Templates</h1>
                    </div>
                    {!loading && totalItems !== undefined && (
                        <div className="font-bold text-lg text-gray-500 dark:text-gray-100">
                            ({totalItems})
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50"
                        title="Refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    <div className="relative w-full sm:w-56">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
                        <input
                            type="text"
                            placeholder="Search templates..."
                            className="w-full bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 outline-none transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <ColumnVisibilityDropdown
                        columns={columnOptions}
                        visibleColumns={visibleColumns}
                        onChange={setVisibleColumns}
                    />

                    {canCreate && (
                        <button
                            onClick={() => {
                                setEditingTemplate(null);
                                setIsAddModalOpen(true);
                            }}
                            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all shadow-lg shadow-orange-900/20 shrink-0 cursor-pointer"
                        >
                            <Plus size={14} />
                            <span>Add Template</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Table Area */}
            <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse table-fixed">
                        <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                {visibleColumns.name && (
                                    <th className="w-[20%] px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
                                        <div className="flex items-center gap-2">
                                            
                                            <span>Template Name</span>
                                        </div>
                                    </th>
                                )}
                                {visibleColumns.event_type && (
                                    <th className="w-[20%] px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
                                        <div className="flex items-center gap-2">
                                          
                                            <span>Event Type</span>
                                        </div>
                                    </th>
                                )}
                                {visibleColumns.is_global && (
                                    <th className="w-[15%] px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
                                        <div className="flex items-center gap-2">
                                            
                                            <span>Global</span>
                                        </div>
                                    </th>
                                )}
                                {visibleColumns.status && (
                                    <th className="w-[15%] px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
                                        <div className="flex items-center gap-2">
                                         
                                            <span>Status</span>
                                        </div>
                                    </th>
                                )}
                                {visibleColumns.created && (
                                    <th className="w-[20%] px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
                                        <div className="flex items-center gap-2">
                                     
                                            <span>Created At</span>
                                        </div>
                                    </th>
                                )}
                                {visibleColumns.actions && (
                                    <th className="w-[10%] px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest text-center">
                                        <span>Actions</span>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-800/60 bg-white dark:bg-gray-900/20 text-xs">
                            {loading && templates.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-12 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest text-[10px]">
                                        <div className="flex flex-col items-center gap-2">
                                            <RefreshCw className="w-5 h-5 animate-spin text-orange-500" />
                                            Loading Templates...
                                        </div>
                                    </td>
                                </tr>
                            ) : templates.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-12 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest text-[10px]">
                                        No event templates found
                                    </td>
                                </tr>
                            ) : (
                                templates.map((template) => (
                                    <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors group">
                                        {visibleColumns.name && (
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 dark:bg-cyan-400/50"></div>
                                                    <span className="text-[11px] font-bold text-gray-900 dark:text-gray-200 tracking-wide">{template.name?.replace(/_/g, ' ')}</span>
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.event_type && (
                                            <td className="px-4 py-2">
                                                <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-400 text-[10px] font-bold uppercase tracking-wider">
                                                    {template.event_type?.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                        )}
                                        {visibleColumns.is_global && (
                                            <td className="px-4 py-2">
                                                {template.is_global ? (
                                                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 text-[9px] font-black uppercase">
                                                        Global
                                                    </div>
                                                ) : (
                                                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 border border-gray-200 dark:border-slate-700 text-[9px] font-black uppercase tracking-tight">
                                                        Local
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                        {visibleColumns.status && (
                                            <td className="px-4 py-2">
                                                {template.is_active ? (
                                                    <div className="inline-flex items-center gap-1 text-emerald-600 dark:text-green-400 font-bold">
                                                        <CheckCircle2 size={12} />
                                                        <span className="text-[10px] uppercase tracking-wider">Active</span>
                                                    </div>
                                                ) : (
                                                    <div className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-bold">
                                                        <XCircle size={12} />
                                                        <span className="text-[10px] uppercase tracking-wider">Inactive</span>
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                        {visibleColumns.created && (
                                            <td className="px-4 py-2">
                                                <div className="flex flex-col">
                                                    <span className="text-gray-700 dark:text-gray-300 font-medium">{formatDate(template.created)}</span>
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.actions && (
                                            <td className="px-4 py-2 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={() => setPreviewingTemplate(template)}
                                                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-all cursor-pointer"
                                                        title="Preview Templates"
                                                    >
                                                        <Eye size={14} />
                                                    </button>

                                                    {canUpdate && (
                                                        <button
                                                            onClick={() => {
                                                                setEditingTemplate(template);
                                                                setIsAddModalOpen(true);
                                                            }}
                                                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 transition-all cursor-pointer"
                                                            title="Edit Template"
                                                        >
                                                            <Edit2 size={14} />
                                                        </button>
                                                    )}

                                                    {canDelete && (
                                                        <button
                                                            onClick={() => setDeleteModal({ isOpen: true, id: template.id })}
                                                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 transition-all cursor-pointer"
                                                            title="Delete Template"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50/80 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-800 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={itemsPerPage}
                            onChange={(val) => {
                                setItemsPerPage(val);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 tracking-widest uppercase">
                            Showing <span className="text-gray-900 dark:text-gray-200">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="text-gray-900 dark:text-gray-200">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="text-gray-900 dark:text-gray-200 font-bold">{totalItems}</span>
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                        >
                            <ChevronsLeft size={14} />
                        </button>
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-sm dark:shadow-lg dark:shadow-orange-900/20">
                            {currentPage}
                        </div>
                        <button
                            disabled={currentPage * itemsPerPage >= totalItems}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                        >
                            <ChevronRight size={14} />
                        </button>
                        <button
                            disabled={currentPage * itemsPerPage >= totalItems}
                            onClick={() => setCurrentPage(Math.ceil(totalItems / itemsPerPage))}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                        >
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>

            <AddTemplateModal
                isOpen={isAddModalOpen}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setEditingTemplate(null);
                }}
                onSuccess={fetchTemplates}
                editingTemplate={editingTemplate}
            />

            {previewingTemplate && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-8 animate-in fade-in duration-200">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPreviewingTemplate(null)} />
                    <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl w-full max-w-6xl h-[85vh] relative flex flex-col overflow-hidden zoom-in-95">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                            <div className="flex items-center gap-3">
                                <div className="bg-cyan-500/10 dark:bg-cyan-600/20 p-2 rounded-xl border border-cyan-500/30">
                                    <Monitor size={20} className="text-cyan-600 dark:text-cyan-400" />
                                </div>
                                <div className="flex flex-col">
                                    <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Template Preview</h2>
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">{previewingTemplate.name}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setPreviewingTemplate(null)}
                                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 rounded-xl transition-all shadow-sm cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex-1 p-6 overflow-hidden flex gap-6 bg-gray-50/50 dark:bg-transparent">
                            {previewingTemplate.ticket_open_template && (
                                <div className="flex-1 flex flex-col gap-3 min-w-0">
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-900/80 rounded-lg border border-gray-200 dark:border-gray-800 w-fit">
                                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                                        <span className="text-[10px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-widest">Ticket Open View</span>
                                    </div>
                                    <div className="flex-1 bg-white rounded-2xl overflow-hidden shadow-md dark:shadow-2xl border border-gray-200 dark:border-gray-800">
                                        <iframe
                                            title="Open Preview"
                                            srcDoc={previewingTemplate.ticket_open_template}
                                            className="w-full h-full border-none"
                                        />
                                    </div>
                                </div>
                            )}

                            {previewingTemplate.ticket_closure_template && previewingTemplate.ticket_closure_template !== 'string' && (
                                <div className="flex-1 flex flex-col gap-3 min-w-0 font-bold">
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-900/80 rounded-lg border border-gray-200 dark:border-gray-800 w-fit">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                        <span className="text-[10px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-widest">Ticket Closure View</span>
                                    </div>
                                    <div className="flex-1 bg-white rounded-2xl overflow-hidden shadow-md dark:shadow-2xl border border-gray-200 dark:border-gray-800">
                                        <iframe
                                            title="Closure Preview"
                                            srcDoc={previewingTemplate.ticket_closure_template}
                                            className="w-full h-full border-none"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteModal({ isOpen: false, id: null })} />
                    <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl w-full max-w-sm relative overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl">
                                    <AlertCircle className="w-6 h-6 text-rose-500" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Delete Template?</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This action cannot be undone and will remove this template permanently.</p>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setDeleteModal({ isOpen: false, id: null })}
                                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold text-[11px] uppercase tracking-widest rounded-lg transition-all cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteTemplate}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-[11px] uppercase tracking-widest rounded-lg transition-all shadow-lg shadow-rose-900/20 flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    {deleting ? (
                                        <>
                                            <RefreshCw size={14} className="animate-spin" />
                                            <span>Deleting...</span>
                                        </>
                                    ) : (
                                        <span>Delete</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventTemplates;
